import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  CheckCircle, 
  Star, 
  Award, 
  Heart, 
  Users,
  Bell,
  TrendingUp
} from 'lucide-react';
import { getTimeAgo } from '../utils/profileHelpers';
import { useAuth } from '../contexts/AuthContext';

const ActivityFeedItem = ({ activity, showUserInfo = false, userProfile = null }) => {
  const { user } = useAuth();

  // Bookings have no standalone detail route — send each role to their own list
  const relatedLink = activity.activityType?.includes('booking')
    ? (user?.role === 'caregiver' ? '/caregiver-bookings' : '/owner-bookings')
    : `/profile/${activity.relatedId}`;

  // Get icon based on activity type
  const getIcon = () => {
    const iconProps = { size: 20 };
    
    switch (activity.activityType) {
      case 'booking_created':
        return <Calendar {...iconProps} className="text-blue-600" />;
      case 'booking_completed':
        return <CheckCircle {...iconProps} className="text-green-600" />;
      case 'review_received':
        return <Star {...iconProps} className="text-yellow-500" />;
      case 'badge_earned':
        return <Award {...iconProps} className="text-[var(--pf-accent)]" />;
      case 'pet_added':
        return <Heart {...iconProps} className="text-pink-600" />;
      case 'follower_milestone':
        return <Users {...iconProps} className="text-indigo-600" />;
      default:
        return <Bell {...iconProps} className="text-gray-600" />;
    }
  };

  // Get background color based on activity type
  const getBgColor = () => {
    switch (activity.activityType) {
      case 'booking_created':
      case 'booking_completed':
        return 'bg-blue-50';
      case 'review_received':
        return 'bg-yellow-50';
      case 'badge_earned':
        return 'bg-[var(--pf-accent-soft)]';
      case 'pet_added':
        return 'bg-pink-50';
      case 'follower_milestone':
        return 'bg-indigo-50';
      default:
        return 'bg-gray-50';
    }
  };

  const timeAgo = getTimeAgo(activity.timestamp || activity.createdAt);

  return (
    <div className={`flex items-start gap-4 p-4 rounded-xl ${getBgColor()} hover:shadow-md transition-shadow duration-200`}>
      {/* Icon */}
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
        {getIcon()}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* User info (if showing on public feed) */}
        {showUserInfo && userProfile && (
          <div className="flex items-center gap-2 mb-1">
            {userProfile.profilePhoto ? (
              <img 
                src={userProfile.profilePhoto} 
                alt={userProfile.alias || 'User'} 
                className="w-6 h-6 rounded-full object-cover"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-[var(--pf-accent)] flex items-center justify-center text-white text-xs font-semibold">
                {(userProfile.alias || 'U').charAt(0).toUpperCase()}
              </div>
            )}
            <Link 
              to={`/profile/${activity.userId}`}
              className="text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors"
            >
              {userProfile.alias || 'User'}
            </Link>
          </div>
        )}

        {/* Description */}
        <p className="text-gray-800 text-sm leading-relaxed">
          {activity.description}
        </p>

        {/* Timestamp */}
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <TrendingUp size={12} />
          {timeAgo}
        </p>
      </div>

      {/* Related link (if applicable) */}
      {activity.relatedId && (
        <div className="flex-shrink-0">
          <Link
            to={relatedLink}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            View
          </Link>
        </div>
      )}
    </div>
  );
};

export default ActivityFeedItem;

